import { useEffect, useRef, useState, useCallback } from 'react';
import { useController } from 'react-hook-form';
import { getSolicitudPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";

// Ya no necesitamos la constante ACCESSIBLE_SIGNATURE porque siempre enviaremos una imagen
// Mantenemos exportada por si hay código que la use
export const ACCESSIBLE_SIGNATURE = 'FIRMA_ACCESIBLE_ELECTRONICA';

function Signer({
  height = 150,
  width = 400,
  style = {},
  control,
  name,
  valor = null,
  rules,
  placeholder = '📝',
  setValue,
  nombreCompleto = '', // Nombre del usuario para firma accesible
}) {
  const {
    field: { ref, value },
  } = useController({
    name,
    control,
    rules,
  });

  const [canvasClean, setCanvasClean] = useState(false);
  const [canvasData, setCanvasData] = useState(null);
  const [signatureComplete, setSignatureComplete] = useState(false);
  const [defaultData, setDefaultData] = useState(null);
  const [fontLoaded, setFontLoaded] = useState(false);

  const { locale } = useLocale();
  const [solicitudPage, setSolicitudPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [accessibleMode, setAccessibleMode] = useState(false);
  const [accessibleSignatureGenerated, setAccessibleSignatureGenerated] = useState(false);

  const refCanvas = useRef();
  const refAccessibleCanvas = useRef();
  const buttonContainer = useRef();

  // Cargar la fuente cursiva para la firma
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    // Esperar a que la fuente se cargue
    document.fonts.ready.then(() => {
      document.fonts.load('48px "Dancing Script"').then(() => {
        setFontLoaded(true);
      }).catch(() => {
        setFontLoaded(true); // Continuar aunque falle
      });
    });

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Función para generar la firma con el nombre del usuario
  const generateAccessibleSignature = useCallback(() => {
    if (!nombreCompleto || !fontLoaded) return null;

    // Crear un canvas temporal para generar la firma
    const canvas = document.createElement('canvas');
    canvas.width = width || 400;
    canvas.height = height || 150;
    const ctx = canvas.getContext('2d');

    // Fondo blanco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Configurar el estilo de la firma
    ctx.fillStyle = '#1a1a2e'; // Color azul oscuro como tinta
    ctx.font = 'italic 48px "Dancing Script", cursive';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Calcular el tamaño óptimo de fuente para que quepa el nombre
    let fontSize = 48;
    const maxWidth = canvas.width - 40;
    ctx.font = `italic ${fontSize}px "Dancing Script", cursive`;
    
    while (ctx.measureText(nombreCompleto).width > maxWidth && fontSize > 20) {
      fontSize -= 2;
      ctx.font = `italic ${fontSize}px "Dancing Script", cursive`;
    }

    // Dibujar el nombre centrado
    const x = canvas.width / 2;
    const y = canvas.height / 2;
    
    // Agregar un ligero efecto de sombra para dar profundidad
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    ctx.fillText(nombreCompleto, x, y);

    // Agregar una línea decorativa debajo
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 1;
    ctx.beginPath();
    const textWidth = ctx.measureText(nombreCompleto).width;
    ctx.moveTo(x - textWidth / 2 - 10, y + fontSize / 2 + 5);
    ctx.lineTo(x + textWidth / 2 + 10, y + fontSize / 2 + 5);
    ctx.stroke();

    return canvas.toDataURL('image/png');
  }, [nombreCompleto, fontLoaded, width, height]);

  // Generar y mostrar la firma accesible cuando se confirma
  const handleAccessibleConfirm = useCallback((checked) => {
    if (checked && nombreCompleto) {
      const signatureDataUrl = generateAccessibleSignature();
      if (signatureDataUrl) {
        setValue(name, signatureDataUrl);
        setSignatureComplete(true);
        setAccessibleSignatureGenerated(true);
        
        // Dibujar la firma en el canvas de preview
        if (refAccessibleCanvas.current) {
          const ctx = refAccessibleCanvas.current.getContext('2d');
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, refAccessibleCanvas.current.width, refAccessibleCanvas.current.height);
            ctx.drawImage(img, 0, 0);
          };
          img.src = signatureDataUrl;
        }
      }
    } else {
      setValue(name, null);
      setSignatureComplete(false);
      setAccessibleSignatureGenerated(false);
      
      // Limpiar el canvas de preview
      if (refAccessibleCanvas.current) {
        const ctx = refAccessibleCanvas.current.getContext('2d');
        ctx.clearRect(0, 0, refAccessibleCanvas.current.width, refAccessibleCanvas.current.height);
      }
    }
  }, [generateAccessibleSignature, nombreCompleto, setValue, name]);

  useEffect(() => {
    // Solo ejecutar lógica del canvas si NO estamos en modo accesible
    if (accessibleMode) return;

    setDefaultData(valor);
    drawImage(valor);
  }, [valor, accessibleMode]);

  const drawImage = (src) => {
    if (accessibleMode || !src || src === ACCESSIBLE_SIGNATURE || src === true) return;
    
    var canvas = refCanvas.current;
    if (!canvas) return;
    
    var obj = new Image();
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (src != null && src != undefined) {
      setSignatureComplete(true);
      obj.src = src;
      obj.onload = setTimeout(() => {
        ctx.drawImage(obj, 0, 0);
      }, 500);
    }
  };

  useEffect(() => {
    // No ejecutar lógica del canvas si estamos en modo accesible
    if (accessibleMode) return;

    var canvas = refCanvas.current;
    if (!canvas) return;

    var ctx = canvas.getContext('2d');
    var drawing = false;
    var clean = true;

    const clear = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const init = () => {
      ctx.font = '40px arial';
      ctx.fillStyle = '#dddddd';
      clear();
      if (!signatureComplete) {
        ctx.fillText(
          placeholder,
          canvas.width / 2 - ctx.measureText(placeholder).width / 2,
          canvas.height / 2
        );
      }
      if (buttonContainer.current) {
        buttonContainer.current.hidden = true;
      }
      clean = true;
    };
    init();

    function drawstart(e) {
      if (!signatureComplete) {
        if (clean) clear();
        clean = false;
        drawing = true;
        ctx.beginPath();
      }
    }
    function drawmove(e) {
      let bounds = canvas.getBoundingClientRect();

      let scaleX = canvas.width / bounds.width;
      let scaleY = canvas.height / bounds.height;

      let pos = {
        x: (e.pageX - bounds.left) * scaleX,
        y: (e.pageY - (bounds.top + window.scrollY)) * scaleY,
      };
      if (drawing) {
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        if (buttonContainer.current) {
          buttonContainer.current.hidden = false;
        }
      }
    }
    function drawend(e) {
      setCanvasData(canvas.toDataURL());
      drawing = false;
    }
    function touchstart(event) {
      drawstart(event.touches[0]);
    }
    function touchmove(event) {
      drawmove(event.touches[0]);
      event.preventDefault();
    }
    function touchend(event) {
      drawend(event.changedTouches[0]);
    }

    canvas.ontouchstart = touchstart;
    canvas.ontouchmove = touchmove;
    canvas.ontouchend = touchend;

    canvas.onmousedown = drawstart;
    canvas.onmousemove = drawmove;
    canvas.onmouseup = drawend;

    const resetCanvas = () => {
      if (canvasClean) {
        clear();
        setCanvasClean(false);
        setCanvasData(null);
        setValue(name, null);
      }
    };
    resetCanvas();

    return () => {
      setCanvasData(null);
      setDefaultData(null);

      if (canvas) {
        var ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [canvasClean, signatureComplete, defaultData, accessibleMode]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getSolicitudPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setSolicitudPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setSolicitudPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale]);

  return (
    <div
      className="signer"
      style={{
        width,
        height: 'auto',
      }}
    >
      <button
        className="quit-button"
        hidden={!signatureComplete || accessibleMode}
        onClick={(e) => {
          e.preventDefault();
          setSignatureComplete(false);
          setCanvasClean(true);
          setDefaultData(null);
          setValue(name, null);
        }}
        aria-label="Borrar firma"
      >
        <i className="material-icons">delete</i>
      </button>
      
      <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={accessibleMode}
          onChange={(e) => {
            setAccessibleMode(e.target.checked);
            // Limpiar el valor al cambiar de modo
            if (!e.target.checked) {
              setValue(name, null);
              setSignatureComplete(false);
              setCanvasClean(true);
              setAccessibleSignatureGenerated(false);
            } else {
              setValue(name, null);
              setSignatureComplete(false);
              setAccessibleSignatureGenerated(false);
            }
          }}
          aria-describedby="signature-help"
        />
        {' '}{solicitudPage?.blind_checkbox || "No puedo firmar dibujando (usar firma accesible)"}
      </label>

      <p id="signature-help" style={{ fontSize: '0.85em', color: '#666', margin: '4px 0 12px 0' }}>
        {solicitudPage?.blind_message || "Seleccione esta opción si utiliza lector de pantalla o no puede usar el área de firma."}
      </p>

      {accessibleMode ? (
        <div style={{ 
          border: '2px dashed #ccc', 
          borderRadius: '8px', 
          padding: '20px', 
          textAlign: 'center',
          backgroundColor: accessibleSignatureGenerated ? '#e8f5e9' : '#f5f5f5'
        }}>
          {nombreCompleto ? (
            <>
              <p style={{ marginBottom: '12px', color: '#555' }}>
                {solicitudPage?.blind_preview_label || "Vista previa de su firma:"}
              </p>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '15px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                padding: '10px'
              }}>
                <canvas
                  ref={refAccessibleCanvas}
                  width={width || 400}
                  height={height || 150}
                  style={{ 
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    maxWidth: '100%'
                  }}
                  aria-label="Vista previa de la firma generada"
                />
              </div>
              <label style={{ cursor: 'pointer', display: 'block' }}>
                <input
                  type="checkbox"
                  checked={accessibleSignatureGenerated}
                  onChange={(e) => handleAccessibleConfirm(e.target.checked)}
                  style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                  aria-label="Confirmar firma electrónica"
                />
                <strong>{solicitudPage?.blind_checkbox_confirm || "Confirmo que acepto y firmo este documento electrónicamente"}</strong>
              </label>
              {accessibleSignatureGenerated && (
                <p style={{ color: '#2e7d32', marginTop: '12px', fontWeight: 'bold' }} role="status">
                  ✓ {solicitudPage?.blind_checkbox_confirm_message || "Firma electrónica registrada"}
                </p>
              )}
            </>
          ) : (
            <p style={{ color: '#d32f2f' }}>
              {solicitudPage?.blind_no_name_error || "No se pudo obtener su nombre. Por favor, complete su información personal primero."}
            </p>
          )}
        </div>
      ) : (
        <canvas
          ref={refCanvas}
          height={height}
          width={width}
          aria-label="Área para firmar con el dedo o mouse"
        />
      )}

      <div
        className="signer-button-container"
        ref={buttonContainer}
        hidden={signatureComplete || accessibleMode}
      >
        <button
          className="reset-button"
          onClick={(e) => {
            e.preventDefault();
            setCanvasClean(true);
          }}
          aria-label="Borrar dibujo"
        >
          <i className="material-icons">close</i>
        </button>

        <button
          className="accept-button"
          onClick={(e) => {
            e.preventDefault();
            setValue(name, canvasData);
            drawImage(canvasData);
          }}
          aria-label="Aceptar firma"
        >
          <i className="material-icons">check</i>
        </button>
      </div>
    </div>
  );
}

export default Signer;
